import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ADMIN_QUERY_KEYS } from '@/features/admin/api/query-keys';
import { notificationService } from '@/lib/services/notification.service';
import {
  getAgentOverrides,
  createAgentOverride,
  updateAgentOverride,
} from '../../api/agent-overrides.api';
import type { AgentOverrideMetadata } from '../tools.types';

interface SaveAgentOverrideParams {
  agentId: string;
  // The backend merges field by field, so an omitted field is left alone rather than cleared.
  // Send only the fields the caller may edit: a payload carrying a field their flags withhold
  // is rejected outright.
  metadata: AgentOverrideMetadata;
  workspaceId?: string;
  organizationId?: string;
}

interface SaveAgentOverrideSideEffects {
  reloadCurrentAgentIfActive: (agentId: string) => Promise<void>;
}

export function useSaveAgentOverride({ reloadCurrentAgentIfActive }: SaveAgentOverrideSideEffects) {
  const { t } = useTranslation('admin');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      agentId,
      metadata,
      workspaceId,
      organizationId,
    }: SaveAgentOverrideParams): Promise<string> => {
      const existingOverrides = await getAgentOverrides(agentId);

      // TODO: Backend must fix the generated AgentOverrideDto type. id should be a string.

      // Find the existing override for this scope.
      // Org-level overrides are stored as type ORGANIZATION (scoped per org).
      // Workspace overrides are type WORKSPACE matched by workspaceId.
      const matchingOverride = existingOverrides.find((override) => {
        if (workspaceId) {
          return override.type === 'WORKSPACE' && override.workspaceId === workspaceId;
        }
        return override.type === 'ORGANIZATION' && override.organizationId === organizationId;
      });

      if (matchingOverride) {
        if (!matchingOverride.id || typeof matchingOverride.id !== 'string') {
          throw new Error('Invalid override ID');
        }
        await updateAgentOverride(matchingOverride.id, metadata, workspaceId);
        return agentId;
      }

      await createAgentOverride(agentId, metadata, workspaceId, organizationId);
      return agentId;
    },
    onSuccess: async (agentId) => {
      // Invalidate rather than remove. `removeQueries` deletes the entry outright, which leaves
      // a mounted observer showing its last render with nothing scheduled to replace it, and
      // means a later `invalidateQueries` on the same key matches nothing. That is what kept
      // the agent config page drawing the category it had moved away from until a reload.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.tools.agents }),
        queryClient.invalidateQueries({
          queryKey: [ADMIN_QUERY_KEYS.tools.agents, 'overrides', agentId],
        }),
        // `agent-config` is a sibling of `agents`, not a child, so the line above does not reach
        // it. It backs the config page's breadcrumb and agent tree icons.
        queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.tools.agentConfig(agentId) }),
        queryClient.invalidateQueries({ queryKey: ['springboard-flow-digest-display'] }),
        // Every single-agent fetch, whatever workspace it is keyed under: the chat Intro and the
        // admin breadcrumbs read the agent's category from here.
        queryClient.invalidateQueries({ queryKey: ['agent'] }),
        // Plural: a different cache from ['agent', id], holding the lists the dashboard
        // shortcuts and the chat tool switcher paint from.
        queryClient.invalidateQueries({ queryKey: ['agents'] }),
        queryClient.invalidateQueries({ queryKey: ['tool-categories'] }),
        queryClient.invalidateQueries({ queryKey: ['springboard-tools'] }),
      ]);

      await reloadCurrentAgentIfActive(agentId);
    },
    onError: () => {
      notificationService.showError(t('admin.useSaveAgentOverride.couldNotSaveToolDisplay'));
    },
  });
}
